/**
 * Landmark fetcher — OpenStreetMap Overpass API + Wikidata sitelinks ranking.
 *
 * Two-phase:
 *   1. fetchLandmarks(bbox) — Overpass query for named POIs. Fast-ish (1-3s).
 *   2. rankByFame(landmarks) — Wikidata batch query for sitelinks count, used
 *      to mark the top 10 most-famous landmarks in the set. A landmark with
 *      many sitelinks (Wikipedia language editions) is globally notable.
 *
 * Non-famous landmarks with a wikipedia/wikidata tag are still marked
 * `notable` as a weaker signal for the UI.
 */

export interface Landmark {
  id: string;
  name: string;
  lat: number;
  lon: number;
  notable: boolean; // has wikipedia or wikidata tag
  wikidataId?: string; // e.g. "Q243"
  fameRank?: number; // 1-10 if in top-10 by Wikidata sitelinks (badge marker)
  siteLinks?: number; // raw Wikipedia language-edition count (sort key)
}

interface OverpassElement {
  type: "node" | "way" | "relation";
  id: number;
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
  tags?: Record<string, string>;
}

interface OverpassResponse {
  elements: OverpassElement[];
}

// Public Overpass mirrors. All carry full global OSM data. Tried in order
// with a client-side timeout per mirror so a slow or hanging one (the
// primary overpass-api.de is frequently 504) falls through rather than
// blocking the UI.
//
// Observed behaviour (April 2026):
//  - overpass.openstreetmap.fr: fast, reliable (~2 s typical)
//  - overpass.kumi.systems:     reliable but sometimes slow (~10 s)
//  - overpass.private.coffee:   intermittently hangs
//  - overpass-api.de:           frequently returns 504 under load
//
// NOT including overpass.osm.ch — Swiss-chapter mirror, returns 200 with 0
// elements for queries outside Switzerland.
const OVERPASS_URLS = [
  "https://overpass.openstreetmap.fr/api/interpreter",
  "https://overpass.kumi.systems/api/interpreter",
  "https://overpass.private.coffee/api/interpreter",
  "https://overpass-api.de/api/interpreter",
];
const PER_MIRROR_TIMEOUT_MS = 12_000;
const WIKIDATA_URL = "https://www.wikidata.org/w/api.php";

// Tag filters — scoped to the five categories the user asked for:
// monuments, bridges, museums, parks, forests.
//
// Dropped tourism=attraction (too generic — catches every playground,
// ferris wheel, info sign) and amenity=place_of_worship (too many minor
// churches). Major cathedrals/basilicas are still caught via
// historic=cathedral|basilica|church|monastery.

// Apply to nodes, ways, AND relations.
const POLYMORPHIC_FILTERS = [
  // Monuments — historic structures + palaces/castles + major religious landmarks
  `["name"]["historic"~"^(monument|memorial|castle|palace|fort|tower|archaeological_site|cathedral|basilica|church|monastery)$"]`,
  // Museums + galleries + aquariums
  `["name"]["tourism"~"^(museum|gallery|aquarium)$"]`,
  // Parks + gardens + nature reserves
  `["name"]["leisure"~"^(park|garden|nature_reserve)$"]`,
  // tourism=attraction is a grab bag ("every playground") so we only include
  // the ones that also have a wikidata tag. This is how the Panthéon
  // (tourism=attraction + building=civic, no historic=*) and similar civic
  // landmarks get picked up without flooding with generic attractions.
  `["name"]["tourism"="attraction"]["wikidata"]`,
];

// Way + relation only — these are always polygons/lines, not points
const AREA_ONLY_FILTERS = [
  // Forests
  `["name"]["landuse"="forest"]`,
  `["name"]["natural"="wood"]`,
  // Bridges — only named bridges with wikidata/wikipedia to avoid flooding
  // the list with every highway bridge in a city
  `["name"]["bridge"="yes"]["wikidata"]`,
  `["name"]["man_made"="bridge"]`,
];

const QUERY_TEMPLATE = (s: number, w: number, n: number, e: number): string => {
  const bbox = `(${s},${w},${n},${e})`;
  const lines: string[] = [];
  for (const kind of ["node", "way", "relation"] as const) {
    for (const filter of POLYMORPHIC_FILTERS) {
      lines.push(`  ${kind}${filter}${bbox};`);
    }
  }
  for (const kind of ["way", "relation"] as const) {
    for (const filter of AREA_ONLY_FILTERS) {
      lines.push(`  ${kind}${filter}${bbox};`);
    }
  }
  return `[out:json][timeout:30];\n(\n${lines.join("\n")}\n);\nout center;`;
};

// NOTE: fetchLandmarks intentionally returns ALL deduped landmarks — no
// pre-ranking cap. A cap here would cut famous landmarks alphabetically
// before they get a chance to be ranked by Wikidata sitelinks (e.g. Paris
// has 500+ named landmarks; "Tour Eiffel" and "Louvre" come after the
// 500-C cutoff). The UI can slice for display; the Wikidata ranker batches
// 50 IDs per call so even 1000 landmarks is ~2 s of extra latency.
const WIKIDATA_BATCH_SIZE = 50;
const TOP_FAMOUS = 10;
const WIKIDATA_ID_RE = /^Q\d+$/;

export async function fetchLandmarks(
  lat_min: number,
  lat_max: number,
  lon_min: number,
  lon_max: number,
  signal?: AbortSignal,
): Promise<Landmark[]> {
  const body = `data=${encodeURIComponent(
    QUERY_TEMPLATE(lat_min, lon_min, lat_max, lon_max),
  )}`;

  const json = await queryOverpassWithFallback(body, signal);

  // Dedupe by lowercase name — multiple OSM tags often attach to the same place.
  const byName = new Map<string, Landmark>();
  for (const elem of json.elements) {
    const name = elem.tags?.name;
    if (!name) continue;
    const lat = elem.lat ?? elem.center?.lat;
    const lon = elem.lon ?? elem.center?.lon;
    if (lat === undefined || lon === undefined) continue;

    const rawWikidata = elem.tags?.wikidata?.trim();
    const wikidataId = rawWikidata && WIKIDATA_ID_RE.test(rawWikidata) ? rawWikidata : undefined;
    const notable = Boolean(elem.tags?.wikipedia || wikidataId);

    const key = name.toLowerCase().trim();
    const existing = byName.get(key);
    // Prefer the better-tagged duplicate.
    if (
      !existing ||
      (wikidataId && !existing.wikidataId) ||
      (notable && !existing.notable)
    ) {
      byName.set(key, {
        id: `${elem.type}/${elem.id}`,
        name,
        lat,
        lon,
        notable,
        wikidataId,
      });
    }
  }

  // Three-tier priority (wikidata > wikipedia-only > plain). No slice — the
  // caller decides how many to display; rankByFame needs all of them first.
  return [...byName.values()].sort((a, b) => {
    const at = a.wikidataId ? 2 : a.notable ? 1 : 0;
    const bt = b.wikidataId ? 2 : b.notable ? 1 : 0;
    if (at !== bt) return bt - at;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Enrich landmarks with a fameRank (1..10) for the globally most-famous
 * ones in the set, measured by Wikipedia language-edition count (sitelinks).
 *
 * Skips landmarks without a wikidata tag. Returns a new array; does not
 * mutate the input. On any failure, returns the input unchanged.
 */
export async function rankByFame(
  landmarks: Landmark[],
  signal?: AbortSignal,
): Promise<Landmark[]> {
  const withWikidata = landmarks.filter((l) => l.wikidataId);
  if (withWikidata.length === 0) return landmarks;

  try {
    const counts = await fetchSitelinksCounts(
      withWikidata.map((l) => l.wikidataId!),
      signal,
    );

    // Rank the landmarks we actually got counts for.
    const ranked = withWikidata
      .filter((l) => counts.has(l.wikidataId!))
      .sort((a, b) => (counts.get(b.wikidataId!) ?? 0) - (counts.get(a.wikidataId!) ?? 0));

    const topIds = new Map<string, number>();
    ranked.slice(0, TOP_FAMOUS).forEach((l, i) => topIds.set(l.id, i + 1));

    // Attach siteLinks to every wikidata-tagged landmark so sorting beyond
    // the top 10 stays fame-ordered rather than alphabetical — otherwise
    // Jardin des Tuileries / du Luxembourg / Panthéon fall past the UI cap
    // in dense cities because A-I alphabet fills the slots first.
    return landmarks.map((l) => ({
      ...l,
      fameRank: topIds.get(l.id),
      siteLinks: l.wikidataId ? counts.get(l.wikidataId) : undefined,
    }));
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    // Any other Wikidata failure: fall back to the un-enriched list.
    console.warn("[landmarks] Wikidata ranking failed:", err);
    return landmarks;
  }
}

/**
 * Try each Overpass mirror in sequence. Fall through on 504/429/5xx or
 * network errors; only give up if every mirror fails. Aborts are always
 * propagated so the caller's AbortController still works.
 */
async function queryOverpassWithFallback(
  body: string,
  signal?: AbortSignal,
): Promise<OverpassResponse> {
  let lastErr: unknown = null;
  for (const url of OVERPASS_URLS) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    // Per-mirror timeout, chained to the caller's signal.
    const perMirrorAc = new AbortController();
    const timer = setTimeout(() => perMirrorAc.abort(), PER_MIRROR_TIMEOUT_MS);
    const onOuterAbort = () => perMirrorAc.abort();
    signal?.addEventListener("abort", onOuterAbort);

    try {
      const res = await fetch(url, {
        method: "POST",
        body,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        signal: perMirrorAc.signal,
      });
      // Transient: try next mirror.
      if (res.status === 429 || res.status === 502 || res.status === 503 || res.status === 504) {
        lastErr = new Error(`${new URL(url).host} ${res.status}`);
        continue;
      }
      if (!res.ok) throw new Error(`Overpass ${res.status}`);
      return (await res.json()) as OverpassResponse;
    } catch (err) {
      // Caller cancelled → bubble up the abort.
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      // Our per-mirror timeout fired OR network error → try next mirror.
      lastErr =
        (err as Error).name === "AbortError"
          ? new Error(`${new URL(url).host} timed out`)
          : err;
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onOuterAbort);
    }
  }
  throw lastErr instanceof Error
    ? new Error(`All Overpass mirrors failed (last: ${lastErr.message})`)
    : new Error("All Overpass mirrors failed");
}

/** Batch-query Wikidata for sitelinks counts. Handles >50 IDs. */
async function fetchSitelinksCounts(
  ids: string[],
  signal?: AbortSignal,
): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  for (let i = 0; i < ids.length; i += WIKIDATA_BATCH_SIZE) {
    const batch = ids.slice(i, i + WIKIDATA_BATCH_SIZE);
    const url = new URL(WIKIDATA_URL);
    url.searchParams.set("action", "wbgetentities");
    url.searchParams.set("ids", batch.join("|"));
    url.searchParams.set("props", "sitelinks");
    url.searchParams.set("format", "json");
    url.searchParams.set("origin", "*"); // CORS anonymous

    const res = await fetch(url.toString(), { signal });
    if (!res.ok) throw new Error(`Wikidata ${res.status}`);
    const json = (await res.json()) as {
      entities?: Record<string, { sitelinks?: Record<string, unknown> }>;
    };

    for (const [id, entity] of Object.entries(json.entities ?? {})) {
      if (!entity.sitelinks) continue;
      counts.set(id, Object.keys(entity.sitelinks).length);
    }
  }
  return counts;
}

/**
 * Sort order:
 *   1. Top 10 (by fameRank 1..10) — guaranteed first, preserving the
 *      explicit ranking the badge shows.
 *   2. Everything else with a siteLinks count, descending (so Tuileries,
 *      Luxembourg, Panthéon etc. float up by real global fame).
 *   3. Notable (wikipedia-only) alphabetical.
 *   4. Plain landmarks alphabetical.
 */
export function sortLandmarks(landmarks: Landmark[]): Landmark[] {
  return [...landmarks].sort((a, b) => {
    if (a.fameRank && b.fameRank) return a.fameRank - b.fameRank;
    if (a.fameRank) return -1;
    if (b.fameRank) return 1;

    const aSL = a.siteLinks ?? -1;
    const bSL = b.siteLinks ?? -1;
    if (aSL !== bSL) return bSL - aSL;

    if (a.notable !== b.notable) return a.notable ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}
